import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import useTranslation from 'next-translate/useTranslation';
import DialogCheckout, {
  CheckoutContext,
} from '#modules/dialog-checkout/DialogCheckout';
import { useRouter } from 'next/router';
import { useGetMediaForCheckoutQuery } from '#types/generated/apollo-client';
import axios, { CancelTokenSource } from 'axios';
import { setCookie } from 'cookies-next';
import logger from '#utils/logger';
import { Product, ProductType } from '#types/generated/graphql-types';

type Props = {
  open: boolean;
  onClose: () => void;
  mediaIri: string;
  productIri: string;
  context: CheckoutContext;
};

export const CheckoutErrorMessageMap = {
  reservation_error: 'checkoutGeneralError',
  persistence_error: 'checkoutGeneralError',
  media_image_fetch_error: 'checkoutGeneralError',
  payment_request_error: 'checkoutPaymentError',
  order_already_exists: 'checkoutOrderExistsError',
  geo_restriction_error: 'checkoutRegionNotAllowedError',
};

const CANCELED_BY_USER_MESSAGE = 'Operation canceled by the user.';
const CancelTokenFactory = axios.CancelToken;
let cancelTokenSource: CancelTokenSource | null;

const RentNotificationDialogContext = createContext(null);

export const RentNotificationDialogProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [mediaIri, setMediaIri] = useState(null);
  const [productIri, setProductIri] = useState(null);
  const [checkoutContext, setCheckoutContext] = useState(null);
  const onCloseRef = useRef<(() => void) | undefined>();

  const handleDialogClose = () => {
    setOpen(false);
    onCloseRef.current?.();
  };

  const showRentNotificationDialog = useCallback(
    (
      media: string,
      product: string,
      onClose?: () => void,
      context: CheckoutContext = CheckoutContext.general
    ) => {
      setOpen(true);
      setMediaIri(media);
      setProductIri(product);
      onCloseRef.current = onClose;
      setCheckoutContext(context);
    },
    []
  );

  return (
    <RentNotificationDialogContext.Provider
      value={{
        open,
        showRentNotificationDialog,
      }}
    >
      {children}
      <DialogRentNotification
        open={open}
        onClose={handleDialogClose}
        mediaIri={mediaIri}
        productIri={productIri}
        context={checkoutContext}
      />
    </RentNotificationDialogContext.Provider>
  );
};

export const useRentNotificationDialog = () =>
  React.useContext(RentNotificationDialogContext);

function DialogRentNotification({
  open,
  onClose,
  mediaIri,
  productIri,
  context,
}: Props) {
  const { lang: locale, t } = useTranslation('common');
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { data } = useGetMediaForCheckoutQuery({
    variables: {
      mediaIri,
    },
    skip: !mediaIri,
  });

  const _products = data?.media.products.availableProducts ?? [];
  const rentProduct: Product = _products.find(
    (product) => product?.type === ProductType.Rent
  );
  let product = _products.find((_product) => _product?.iri === productIri);
  if (!product) {
    product = rentProduct;
  }

  useEffect(() => {
    if (mediaIri) {
      setErrorMessage(undefined);
    }
  }, [mediaIri]);

  if (!product) {
    return null;
  }

  const handleCancel = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel(CANCELED_BY_USER_MESSAGE);
    }
    onClose();
    setLoading(false);
  };

  const handleConfirm = () => {
    setLoading(true);
    setErrorMessage(undefined);
    const sourceUrl = `/${router.locale}${router.asPath}`;
    cancelTokenSource = CancelTokenFactory.source();

    axios
      .post(
        '/api/service/checkout',
        {
          locale,
          mediaIri,
          productIri: rentProduct.iri,
          sourceUrl,
        },

        {
          headers: { 'Content-Type': 'application/ld+json' },
          cancelToken: cancelTokenSource.token,
        }
      )
      .then((res) => {
        const { checkoutUri, orderId } = res.data;
        cancelTokenSource = null;
        setCookie('orderId', orderId);
        setLoading(false);
        window.location.href = checkoutUri;
      })
      .catch((error) => {
        cancelTokenSource = null;

        if (error.message !== CANCELED_BY_USER_MESSAGE) {
          const errorMessageI18nKey =
            CheckoutErrorMessageMap[error.response.data.statusText] ||
            'checkoutGeneralError';
          setLoading(false);
          setErrorMessage(t(errorMessageI18nKey));
          logger.error('Error on movie checkout', {
            locale,
            message: error.message,
          });
        }
      });
  };

  return (
    <DialogCheckout
      open={open}
      loading={loading}
      error={errorMessage}
      title={data?.media?.title}
      mediaDuration={data?.media?.mediaDuration}
      mediaQuality={data?.media?.mediaQuality}
      productLifetime={product.lifetime}
      productPrice={product.price}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      context={context}
    />
  );
}

export default DialogRentNotification;
